import React, { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import { Eye, Minus, Plus, Download } from "lucide-react";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../../components/common/GeneralComponent";
import { fetchData, formatDateDMY, formatDateTimeDMY } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Pagination,
} from "@mui/material";
import { toast } from "react-toastify";
import Loader from "../../../components/partials/Loader";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";
import GrantImportButtons from "../GrantImportButtons";
import { exportMicroGrants } from "../grantExcelExport";
import { IP_DISTRICT_MAP, ROLE_TO_IP_NAME } from "../ipDistrictMapping";

const MicroGrantList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];
  const isAdmin = loggedInUser?.user_role_name === "Super Admin";
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [grants, setGrants] = useState([]);
  const [filterKey, setFilterKey] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [loader, setLoader] = useState(true);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasFilters, setHasFilters] = useState(false);

  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [tehsils, setTehsils] = useState([]);
  const [selectedTehsil, setSelectedTehsil] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedBusinessSector, setSelectedBusinessSector] = useState("");
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [selectedIpsGroup, setSelectedIpsGroup] = useState("");

  // Temporary filter states for Apply button functionality
  const [tempDistrict, setTempDistrict] = useState(null);
  const [tempTehsils, setTempTehsils] = useState([]);
  const [tempTehsil, setTempTehsil] = useState(null);
  const [tempStatus, setTempStatus] = useState("");
  const [tempBusinessSector, setTempBusinessSector] = useState("");
  const [tempCycle, setTempCycle] = useState(null);
  const [tempIpsGroup, setTempIpsGroup] = useState("");

  const [cycles, setCycles] = useState([]);
  const [ipsGroups] = useState([
    { label: "SRSO", value: "SRSO" },
    { label: "TRDP", value: "TRDP" },
    { label: "ACT International", value: "ACT INTL" },
    { label: "NRSP", value: "NRSP" },
    { label: "PMN", value: "PMN" },
  ]);

  const [updatingQualification, setUpdatingQualification] = useState(new Set());
  const [isQualifyModalVisible, setQualifyModalVisible] = useState(false);
  const [isNotQualifyModalVisible, setNotQualifyModalVisible] = useState(false);
  const [selectedQualifyId, setSelectedQualifyId] = useState(null);
  const [selectedNotQualifyId, setSelectedNotQualifyId] = useState(null);

  const checkFiltersApplied = () => {
    const hasActiveFilters = !!(
      search ||
      selectedDistrict ||
      selectedTehsil ||
      selectedStatus ||
      selectedBusinessSector ||
      selectedCycle ||
      selectedIpsGroup
    );
    setHasFilters(hasActiveFilters);
  };

  useEffect(() => {
    checkFiltersApplied();
  }, [
    search,
    selectedDistrict,
    selectedTehsil,
    selectedStatus,
    selectedBusinessSector,
    selectedCycle,
    selectedIpsGroup,
  ]);

  // Main fetch function with current filters
  const fetchGrants = useCallback(async () => {
    try {
      setLoader(true);
      const response = await fetchData(
        "micro-grants",
        "GET",
        null,
        {},
        {
          search,
          page: pagination.currentPage,
          limit: pagination.pageSize,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
          overall_status: selectedStatus || undefined,
          business_sector: selectedBusinessSector || undefined,
          cycle_id: selectedCycle || undefined,
          ips_group: selectedIpsGroup || undefined,
        },
        navigate,
        "micro-grant",
        dispatch
      );

      setLoader(false);
      setGrants(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      setLoader(false);
      toast.error("Failed to fetch Micro grants.");
    }
  }, [
    search,
    pagination.currentPage,
    pagination.pageSize,
    selectedDistrict,
    selectedTehsil,
    selectedStatus,
    selectedBusinessSector,
    selectedCycle,
    selectedIpsGroup,
    navigate,
    dispatch,
  ]);

  // Fetch data when main filters change
  useEffect(() => {
    fetchGrants();
  }, [fetchGrants]);

  const fetchDistricts = async () => {
    try {
      const response = await fetchData(
        "districts",
        "GET",
        null,
        {},
        { status: 1 },
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(response?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchTehsils = async (districtId) => {
    if (!districtId) return [];
    try {
      const response = await fetchData(
        "tehsils",
        "GET",
        null,
        {},
        { district_id: districtId, status: 1 },
        navigate,
        "/tehsils",
        dispatch
      );
      return response?.data || [];
    } catch (error) {
      console.error("Error fetching tehsils:", error);
      return [];
    }
  };

  // Fetch tehsils when main district changes
  useEffect(() => {
    const loadTehsils = async () => {
      if (selectedDistrict) {
        const tehsilsData = await fetchTehsils(selectedDistrict);
        setTehsils(tehsilsData);
      } else {
        setTehsils([]);
      }
    };
    loadTehsils();
  }, [selectedDistrict]);

  // Initialize districts
  useEffect(() => {
    fetchDistricts();
  }, []);

  const fetchCycles = async () => {
    try {
      const response = await fetchData(
        "cycle",
        "GET",
        null,
        {},
        { status: 1 },
        navigate,
        "/cycle",
        dispatch
      );
      setCycles(response?.data || []);
    } catch (error) {
      console.error("Error fetching cycles:", error);
    }
  };

  useEffect(() => {
    fetchCycles();
  }, []);

  // Handle Apply Filters button
  const handleApplyFilters = async () => {
    try {
      setLoader(true);

      // Apply the temporary filters to main state
      setSelectedDistrict(tempDistrict);
      setSelectedTehsil(tempTehsil);
      setSelectedStatus(tempStatus);
      setSelectedBusinessSector(tempBusinessSector);
      setSelectedCycle(tempCycle);
      setSelectedIpsGroup(tempIpsGroup);

      // Reset to first page
      setPagination((prev) => ({ ...prev, currentPage: 1 }));

      // Fetch data with the temporary filters immediately
      const response = await fetchData(
        "micro-grants",
        "GET",
        null,
        {},
        {
          search,
          page: 1, // Always start from page 1 when applying new filters
          limit: pagination.pageSize,
          district_id: tempDistrict || undefined,
          tehsil_id: tempTehsil || undefined,
          overall_status: tempStatus || undefined,
          business_sector: tempBusinessSector || undefined,
          cycle_id: tempCycle || undefined,
          ips_group: tempIpsGroup || undefined,
        },
        navigate,
        "micro-grant",
        dispatch
      );

      setLoader(false);
      setGrants(response?.data || []);
      setPagination(response?.pagination || {});

      // Force re-render of dropdowns
      setFilterKey((prev) => prev + 1);
    } catch (error) {
      setLoader(false);
      toast.error("Failed to fetch Micro grants.");
    }
  };

  // Handle Clear Filters button
  const handleClearFilters = async () => {
    try {
      setLoader(true);

      // Clear temporary states
      setTempDistrict(null);
      setTempTehsil(null);
      setTempStatus("");
      setTempBusinessSector("");
      setTempCycle(null);
      setTempTehsils([]);
      setTempIpsGroup("");

      // Clear main states
      setSelectedDistrict(null);
      setSelectedTehsil(null);
      setSelectedStatus("");
      setSelectedBusinessSector("");
      setSelectedCycle(null);
      setSelectedIpsGroup("");

      // Reset pagination
      setPagination((prev) => ({ ...prev, currentPage: 1 }));

      // Fetch data with cleared filters
      const response = await fetchData(
        "micro-grants",
        "GET",
        null,
        {},
        {
          search,
          page: 1,
          limit: pagination.pageSize,
          // No filters applied
        },
        navigate,
        "micro-grant",
        dispatch
      );

      setLoader(false);
      setGrants(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      setLoader(false);
      toast.error("Failed to fetch Micro grants.");
    }

    // Force re-render of dropdowns
    setFilterKey((prev) => prev + 1);
  };

  // Handle search input change (for the main search box outside accordion)
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Handle district change in filter UI
  const handleDistrictChange = async (selected) => {
    setTempDistrict(selected || null);
    setTempTehsil(null); // Reset tehsil when district changes

    // Fetch tehsils for the selected district in temp state
    if (selected) {
      const tehsilsData = await fetchTehsils(selected);
      setTempTehsils(tehsilsData);
    } else {
      setTempTehsils([]);
    }
  };

  // Handle tehsil change in filter UI
  const handleTehsilChange = (selected) => {
    setTempTehsil(selected || null);
  };

  // Handle status change in filter UI
  const handleStatusChange = (selected) => {
    setTempStatus(selected || "");
  };

  // Handle business sector change in filter UI
  const handleBusinessSectorChange = (selected) => {
    setTempBusinessSector(selected || "");
  };

  // Handle cycle change in filter UI
  const handleCycleChange = (selected) => {
    setTempCycle(selected || null);
  };

  // Handle IPS Group change in filter UI
  const handleIpsGroupChange = (selected) => {
    setTempIpsGroup(selected || "");

    const ipName = ROLE_TO_IP_NAME[selected];
    const allowedNames = ipName ? IP_DISTRICT_MAP[ipName] || [] : [];
    const matches = districts.filter((d) =>
      allowedNames.includes(d.district_name)
    );

    if (matches.length === 1) {
      handleDistrictChange(matches[0].district_id);
    } else if (!selected) {
      handleDistrictChange(null);
    }
  };

  const filteredFilterDistricts = (() => {
    const ipName = ROLE_TO_IP_NAME[tempIpsGroup];
    if (!ipName) return districts;
    const allowedNames = IP_DISTRICT_MAP[ipName] || [];
    return districts.filter((d) => allowedNames.includes(d.district_name));
  })();

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  const fetchAllGrantsForExport = async () => {
    try {
      const response = await fetchData(
        "micro-grants",
        "GET",
        null,
        {},
        {
          search,
          page: 1,
          limit: 10000,
          district_id: selectedDistrict || undefined,
          tehsil_id: selectedTehsil || undefined,
          overall_status: selectedStatus || undefined,
          business_sector: selectedBusinessSector || undefined,
          cycle_id: selectedCycle || undefined,
          ips_group: selectedIpsGroup || undefined,
        },
        navigate,
        "micro-grant",
        dispatch
      );

      return response?.data || [];
    } catch (error) {
      console.error("Error fetching all grants for export:", error);
      throw error;
    }
  };

  const getBase64ImageFromURL = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.setAttribute("crossOrigin", "anonymous");
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = (error) => reject(error);
      img.src = url;
    });
  };

  const fetchMicroGrantById = async (id, navigate, dispatch) => {
    try {
      const response = await fetchData(
        `micro-grants/${id}`,
        "GET",
        null,
        {},
        {},
        navigate,
        "micro-grant",
        dispatch
      );

      return response?.data;
    } catch (error) {
      console.error("Error fetching micro grant:", error);
      toast.error("Failed to fetch micro grant details");
      return null;
    }
  };

  const handleViewPdf = async (grant) => {
    try {
      const logoRight = await getBase64ImageFromURL(
        require("../../../assets/media/photos/piadar_login_logo.png")
      );
      const logoLeft = await getBase64ImageFromURL(
        require("../../../assets/media/photos/logo1.png")
      );

      const borderedLayout = {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => "#ccc",
        vLineColor: () => "#ccc",
        paddingLeft: () => 6,
        paddingRight: () => 6,
        paddingTop: () => 4,
        paddingBottom: () => 4,
      };

      let fieldVerificationDistrict = "N/A";
      if (grant.field_verification_district) {
        fieldVerificationDistrict =
          grant.field_verification_district.district_name || "N/A";
      } else if (grant.field_verification_district_id && districts.length > 0) {
        const district = districts.find(
          (d) => d.district_id === grant.field_verification_district_id
        );
        fieldVerificationDistrict = district ? district.district_name : "N/A";
      }

      const formatDate = formatDateDMY;
      const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "N/A");
      const hasValue = (v) => v !== null && v !== undefined && v !== "";
      const millions = (v) =>
        hasValue(v)
          ? Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 })
          : "N/A";
      const otherText = (value, other) =>
        value === "Other/Specify" ? `Other - ${other || "N/A"}` : value || "N/A";
      const sdgText = Array.isArray(grant.sdg_interventions) && grant.sdg_interventions.length
        ? grant.sdg_interventions.join(", ")
        : "N/A";
      const ucName = grant.uc?.uc_name || grant.UC?.uc_name || grant.uc_name || "N/A";

      const docDefinition = {
        content: [
          {
            columns: [
              {
                image: logoLeft,
                width: 150,
                alignment: "left",
                margin: [20, 15, 20, 0],
              },
              {
                text: "Micro Business Grant",
                style: "mainHeader",
                alignment: "center",
                margin: [0, 15, 0, 0],
              },
              {
                image: logoRight,
                width: 60,
                alignment: "right",
                margin: [0, 15, 0, 0],
              },
            ],
            columnGap: 10,
            margin: [0, 0, 0, 20],
          },
          { text: "Administrative Information", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                ["Cycle", grant.cycle?.cycle_name || "N/A", "District", grant.district?.district_name || "N/A"],
                ["Tehsil", grant.tehsil?.tehsil_name || grant.Tehsil?.tehsil_name || "N/A", "UC", ucName],
                ["Village", grant.village_name || "N/A", "Latitude", grant.latitude || "N/A"],
                ["Longitude", grant.longitude || "N/A", "", ""],
              ],
            },
          },
          { text: "Beneficiary Details", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                ["Application ID", grant.application_id || "N/A", "Company Name", grant.company_name || "N/A"],
                ["Applicant Name", grant.applicant_full_name || "N/A", "Father/Husband Name", grant.father_husband_name || "N/A"],
                ["CNIC Number", grant.cnic_number || "N/A", "Gender", grant.gender || "N/A"],
                ["Date of Birth", formatDate(grant.date_of_birth) || "N/A", "Person with Disability", yesNo(grant.person_with_disability)],
                ["Marital Status", grant.marital_status || "N/A", "Contact Number", grant.contact_number || "N/A"],
                ["Email", grant.email || "N/A", "Address", grant.address || "N/A"],
              ],
            },
          },
          { text: "Grant Details", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                [
                  "Business Category",
                  grant.business_category || "N/A",
                  "Business Entity Type",
                  otherText(grant.business_entity, grant.business_entity_other),
                ],
                [
                  "Business Sector",
                  otherText(grant.business_sector, grant.business_sector_other),
                  "Business Sub Type",
                  grant.business_sub_type || "N/A",
                ],
                ["SDG Interventions", { text: sdgText, colSpan: 3 }, "", ""],
                [
                  "Grant Asked Amount (PKR) (Million)",
                  millions(grant.grant_asked_amount),
                  "Cost Rationalization Amount (PKR) (Million)",
                  millions(grant.cost_rationalization_amount),
                ],
                ["Item Details / Equipment/Material Provided", grant.item_details || "N/A", "ECIB Clearance", yesNo(grant.ecib_clearance)],
                [
                  "Date of Registration for Grant",
                  formatDate(grant.registration_date) || "N/A",
                  "Date of Application Submission",
                  formatDate(grant.application_submission_date) || "N/A",
                ],
                ["Date of Selection", formatDate(grant.selection_date) || "N/A", "Date of Approval", formatDate(grant.approval_date) || "N/A"],
                ["Date of Disbursement", formatDate(grant.disbursement_date) || "N/A", "", ""],
              ],
            },
          },
          { text: "Verification & Support", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                ["Field Officer Name", grant.field_officer_name || "N/A", "Date of Field Verification", formatDate(grant.field_verification_date) || "N/A"],
                ["Field Verification District", fieldVerificationDistrict, "Technical Assistance Provided", yesNo(grant.technical_assistance_provided)],
                ["Verification Approved", yesNo(grant.verification_approved), "", ""],
                ["Remarks by Field Officer", { text: grant.field_officer_remarks || "N/A", colSpan: 3 }, "", ""],
              ],
            },
          },
          { text: "Overall Progress", style: "tabHeader", headlineLevel: 1 },
          {
            style: "tableBlock",
            layout: borderedLayout,
            table: {
              widths: ["25%", "25%", "25%", "25%"],
              body: [
                ...(grant.initial_overall_status
                  ? [
                      ["Status of Overall Grant (First Entry)", grant.initial_overall_status, "Extended Expiry Date", formatDate(grant.initial_extended_expiry_date) || "N/A"],
                      ["Remarks (First Entry)", { text: grant.initial_overall_remarks || "N/A", colSpan: 3 }, "", ""],
                    ]
                  : []),
                grant.overall_status === "Delayed"
                  ? ["Status of Overall Grant", grant.overall_status, "Extended Expiry Date", formatDate(grant.extended_expiry_date) || "N/A"]
                  : ["Status of Overall Grant", grant.overall_status || "N/A", "Date of Completion", formatDate(grant.completion_date) || "N/A"],
                ["Remarks (Overall)", { text: grant.overall_remarks || "N/A", colSpan: 3 }, "", ""],
              ],
            },
          },
        ],
        pageBreakBefore: (node, followingNodesOnPage) =>
          node.headlineLevel === 1 && followingNodesOnPage.length === 0,
        styles: {
          mainHeader: {
            fontSize: 20,
            bold: true,
            color: "#137e81",
          },
          tabHeader: {
            fontSize: 14,
            bold: true,
            margin: [0, 10, 0, 6],
            color: "#137e81",
            decoration: "underline",
          },
          tableBlock: {
            margin: [0, 0, 0, 15],
          },
          tableHeader: {
            bold: true,
            fontSize: 11,
            fillColor: "#f1f1f1",
          },
        },
        defaultStyle: {
          fontSize: 9,
        },
      };

      pdfMake.createPdf(docDefinition).open();
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const exportData = await fetchAllGrantsForExport();
      if (!exportData.length) {
        toast.warning("No data available to export");
        return;
      }
      exportMicroGrants(exportData, {
        fileBase: `micro-grants${hasFilters ? "-filtered" : ""}`,
        districts,
      });
      toast.success("Micro Grant data exported successfully");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Failed to export data to Excel");
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handleDelete = async () => {
    setModalVisible(false);
    try {
      await fetchData(
        `micro-grants/${selectedId}`,
        "DELETE",
        null,
        {},
        {},
        navigate,
        "/micro-grant",
        dispatch
      );
      toast.success("Micro Grant deleted successfully.");
      fetchGrants();
    } catch (error) {
      toast.error("Failed to delete Micro grant.");
    }
  };

  const showDeleteModal = (id) => {
    setSelectedId(id);
    setModalVisible(true);
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const formatCurrency = (amount) =>
    `Rs ${(Number(amount) || 0).toLocaleString("en-US", { maximumFractionDigits: 6 })} M`;

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-[#137e81]";
      case "In progress":
        return "bg-blue-500";
      case "Delayed":
        return "bg-yellow-500";
      case "Postponed":
        return "bg-red-500";
      case "Cancelled":
        return "bg-orange-500";
      default:
        return "bg-gray-500";
    }
  };

  const formatQualificationDateTime = formatDateTimeDMY;

  const showQualifyModal = (grantId) => {
    setSelectedQualifyId(grantId);
    setQualifyModalVisible(true);
  };

  const showNotQualifyModal = (grantId) => {
    setSelectedNotQualifyId(grantId);
    setNotQualifyModalVisible(true);
  };

  const handleQualify = async () => {
    setQualifyModalVisible(false);
    if (updatingQualification.has(selectedQualifyId)) return;

    setUpdatingQualification((prev) => new Set([...prev, selectedQualifyId]));

    try {
      await fetchData(
        `micro-grants/${selectedQualifyId}/qualification`,
        "PUT",
        { is_qualified: true },
        {},
        {},
        navigate,
        "/micro-grant",
        dispatch
      );

      toast.success("Grant qualified successfully");
      fetchGrants();
    } catch (error) {
      toast.error(`Failed to qualify grant: ${error.message}`);
    } finally {
      setUpdatingQualification((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedQualifyId);
        return newSet;
      });
    }
  };

  const handleNotQualify = async () => {
    setNotQualifyModalVisible(false);
    if (updatingQualification.has(selectedNotQualifyId)) return;

    setUpdatingQualification(
      (prev) => new Set([...prev, selectedNotQualifyId])
    );

    try {
      await fetchData(
        `micro-grants/${selectedNotQualifyId}/qualification`,
        "PUT",
        { is_qualified: false },
        {},
        {},
        navigate,
        "/micro-grant",
        dispatch
      );

      toast.success("Grant marked as not qualified successfully");
      fetchGrants();
    } catch (error) {
      toast.error(`Failed to update qualification status: ${error.message}`);
    } finally {
      setUpdatingQualification((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedNotQualifyId);
        return newSet;
      });
    }
  };

  const columns = [
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Sr.No</span>
      ),
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
    },
    ...(isAdmin ? [
      {
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">IPS User</span>
        ),
        cell: (row) => {
          const creatorName = row.creator?.name || "N/A";
          return <span className="text-sm font-medium">{creatorName}</span>;
        },
        width: "150px",
      },
    ] : []),
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Cycle</span>
      ),
      selector: (row) => row.cycle?.cycle_name || "N/A",
      sortable: true,
      width: "120px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Application ID
        </span>
      ),
      selector: (row) => row.application_id || "N/A",
      sortable: true,
      width: "140px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Company Name
        </span>
      ),
      minWidth: "140px",
      selector: (row) => row.company_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Applicant Name
        </span>
      ),
      minWidth: "140px",
      selector: (row) => row.applicant_full_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">IP Name</span>
      ),
      selector: (row) => row.ip_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">District</span>
      ),
      selector: (row) => row.district?.district_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Tehsil</span>
      ),
      selector: (row) => row.tehsil?.tehsil_name || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Cost Rationalization (M) (PKR)
        </span>
      ),
      selector: (row) => formatCurrency(row.cost_rationalization_amount || 0),
      sortable: true,
      width: "180px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Business Sector
        </span>
      ),
      width: "150px",
      selector: (row) => row.business_sector || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Sector Other Specify Name
        </span>
      ),
      width: "150px",
      selector: (row) =>
        row.business_sector === "Other/Specify"
          ? row.business_sector_other || "N/A"
          : "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Business Entity
        </span>
      ),
      width: "150px",
      selector: (row) => row.business_entity || "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Entity Other Specify Name
        </span>
      ),
      width: "150px",
      selector: (row) =>
        row.business_entity === "Other/Specify"
          ? row.business_entity_other || "N/A"
          : "N/A",
      sortable: true,
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">Status</span>
      ),
      cell: (row) => {
        const status = row.overall_status;
        return (
          <span
            className={`py-1 px-2 text-[0.75rem] rounded-full font-semibold text-white ${getStatusColor(
              status
            )}`}
          >
            {status
              ?.replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          </span>
        );
      },
      width: "120px",
    },
    {
      name: (
        <span className="text-[0.9rem] text-[#212529] uppercase">
          Qualified
        </span>
      ),
      cell: (row) => {
        const isQualified = row.is_qualified === true;
        const isNotQualified = row.is_qualified === false;
        const qualificationDateTime = formatQualificationDateTime(
          row.qualification_datetime
        );

        return (
          <div className="flex flex-col items-center justify-start gap-1 py-2">
            {isQualified ? (
              <>
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-blue-100 text-blue-800 whitespace-nowrap">
                  Qualified
                </span>
                {qualificationDateTime && (
                  <div className="text-[0.50rem] text-gray-600 text-center whitespace-nowrap">
                    {qualificationDateTime}
                  </div>
                )}
              </>
            ) : isNotQualified ? (
              <>
                <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-red-100 text-red-800 whitespace-nowrap">
                  Not Qualified
                </span>
                {qualificationDateTime && (
                  <div className="text-[0.50rem] text-gray-600 text-center whitespace-nowrap">
                    {qualificationDateTime}
                  </div>
                )}
              </>
            ) : (
              <span className="py-1 px-2 text-[0.75rem] rounded-full font-semibold bg-yellow-100 text-yellow-800">
                Pending
              </span>
            )}
          </div>
        );
      },
      width: "160px",
    },
  ];

  {
    if (
      permissions.includes("micro-grant-update") ||
      permissions.includes("micro-grant-approve") ||
      permissions.includes("micro-grant-reject") ||
      permissions.includes("micro-grant-delete")
    ) {
      columns.push({
        name: (
          <span className="text-[0.9rem] text-[#212529] uppercase">Action</span>
        ),
        cell: (row) => (
          <div className="flex gap-1">
            <PermissionButton
              hasPermission={permissions.includes("micro-grant-update")}
              to={`/micro-grant/edit/${row.micro_grant_id}`}
              faIcon="fa-solid fa-pen-to-square fa-lg"
              onlyIcon
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed"
            />
            {permissions.includes("micro-grant-approve") &&
              row.is_qualified !== true && (
                <button
                  onClick={() => showQualifyModal(row.micro_grant_id)}
                  disabled={updatingQualification.has(row.micro_grant_id)}
                  title="Mark as Qualified"
                  className="hover:scale-110 transition-transform"
                >
                  <i className="fa-solid fa-check-circle fa-lg text-green-600 hover:text-green-800"></i>
                </button>
              )}
            {permissions.includes("micro-grant-reject") &&
              row.is_qualified !== false && (
                <button
                  onClick={() => showNotQualifyModal(row.micro_grant_id)}
                  disabled={updatingQualification.has(row.micro_grant_id)}
                  title="Mark as Not Qualified"
                  className="hover:scale-110 transition-transform"
                >
                  <i className="fa-solid fa-times-circle fa-lg text-red-600 hover:text-red-800"></i>
                </button>
              )}

            <button
              onClick={async () => {
                const grant = await fetchMicroGrantById(
                  row.micro_grant_id,
                  navigate,
                  dispatch
                );
                if (grant) handleViewPdf(grant);
              }}
              title="View PDF"
              className="hover:scale-110 transition-transform"
            >
              <Eye className="text-green-600" size={18} />
            </button>
            <PermissionButton
              hasPermission={permissions.includes("micro-grant-delete")}
              faIcon="fa fa-trash fa-lg text-primaryDark"
              onlyIcon
              onClick={() => showDeleteModal(row.micro_grant_id)}
              className=" rounded-full bg-gray-200 text-[#137e81] hover:text-white"
              enabledClass=""
              disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
            />
          </div>
        ),
        width: "180px",
      });
    }
  }

  // const customStyles = {
  //   headCells: {
  //     style: {
  //       border: "1px solid #f3f3f3",
  //       backgroundColor: "#fff",
  //       color: "black",
  //       fontWeight: "bold",
  //       fontSize: "1.075rem",
  //       textAlign: "center",
  //     },
  //   },
  //   cells: {
  //     style: {
  //       border: "1px solid #f3f3f3",
  //       textAlign: "center",
  //     },
  //   },
  // };
    const customStyles = {
      headCells: {
        style: {
          border: "1px solid #f3f3f3",
          backgroundColor: "#fff",
          color: "black",
          fontWeight: "bold",
          fontSize: "1.075rem",
          textAlign: "center",
        },
      },
      cells: {
        style: {
          border: "1px solid #f3f3f3",
          textAlign: "center",
        },
      },
    };


  useEffect(() => {
    setTimeout(() => setLoader(false), 1000);
  }, []);

  const shouldShowExportButton = grants.length > 0;

  return (
    <div className="w-full px-4">
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Micro Grant
          </h1>
          <div className="flex flex-wrap items-center gap-2">
          {(permissions.includes("micro-grant-create") || permissions.includes("micro-grant-import")) && (
            <GrantImportButtons
              importType="micro"
              label="Micro Grant"
              onImported={fetchGrants}
              canDownloadSample={permissions.includes("micro-grant-create")}
              canImport={permissions.includes("micro-grant-import")}
            />
          )}
          <PermissionButton
            hasPermission={permissions.includes("micro-grant-create")}
            to="add"
            faIcon="fa fa-plus"
            text="Add"
            className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
            enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
            disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
          />
          </div>
        </div>
      </div>

      <Accordion>
        <AccordionSummary
          expandIcon={
            isFiltersVisible ? (
              <Minus color="#ffff" size={14} />
            ) : (
              <Plus size={14} color="#ffff" />
            )
          }
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            backgroundColor: "#137e81",
            color: "#fff !important",
            "& .MuiAccordionSummary-content": {
              color: "#fff  !important",
            },
          }}
          onClick={toggleFilters}
        >
          Filters
        </AccordionSummary>
        <AccordionDetails>
          <div className="flex flex-col gap-5 w-full">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {isAdmin && (
                <div>
                  <ThinDropdownWithSearch required={false}
                    key={`ips-group-${filterKey}`}
                    label="IPS"
                    options={ipsGroups}
                    value={tempIpsGroup}
                    onChange={handleIpsGroupChange}
                  />
                </div>
              )}

              <div>
                <ThinDropdownWithSearch required={false}
                  key={`cycle-${filterKey}`}
                  label="Cycle"
                  options={cycles?.map((cycle) => ({
                    value: cycle?.id,
                    label: cycle?.cycle_name,
                  }))}
                  value={tempCycle}
                  onChange={handleCycleChange}
                />
              </div>

              <div>
                <ThinDropdownWithSearch required={false}
                  key={`district-${filterKey}`}
                  label="District"
                  options={filteredFilterDistricts?.map((dis) => ({
                    value: dis?.district_id,
                    label: dis?.district_name,
                  }))}
                  value={tempDistrict}
                  onChange={handleDistrictChange}
                />
              </div>

              <div>
                <ThinDropdownWithSearch required={false}
                  key={`tehsil-${filterKey}`}
                  label="Tehsil"
                  options={tempTehsils?.map((t) => ({
                    value: t?.tehsil_id,
                    label: t?.tehsil_name,
                  }))}
                  value={tempTehsil}
                  onChange={handleTehsilChange}
                  disabled={!tempDistrict}
                />
              </div>

              <div>
                <ThinDropdownWithSearch required={false}
                  key={`status-${filterKey}`}
                  label="Status"
                  options={[
                    { value: "Completed", label: "Completed" },
                    { value: "In progress", label: "In Progress" },
                    { value: "Delayed", label: "Delayed" },
                    { value: "Postponed", label: "Postponed" },
                  ]}
                  value={tempStatus}
                  onChange={handleStatusChange}
                />
              </div>

              <div>
                <ThinDropdownWithSearch required={false}
                  key={`business-sector-${filterKey}`}
                  label="Business Sector"
                  options={[
                    { label: "Aquaculture & Fisheries", value: "Aquaculture & Fisheries" },
                    { label: "Agri-Food & Beverage Processing", value: "Agri-Food & Beverage Processing" },
                    { label: "Livestock, Poultry & Meat Value Chain", value: "Livestock, Poultry & Meat Value Chain" },
                    { label: "Textiles, Apparel & Handicrafts", value: "Textiles, Apparel & Handicrafts" },
                    { label: "Automotive & Light Engineering", value: "Automotive & Light Engineering" },
                    { label: "Construction Materials & Prefabrication", value: "Construction Materials & Prefabrication" },
                    { label: "Renewable Energy & Energy Services", value: "Renewable Energy & Energy Services" },
                    { label: "Health & Care Services", value: "Health & Care Services" },
                    { label: "Education, Training & EdTech", value: "Education, Training & EdTech" },
                    { label: "Tourism, Hospitality & Creative Services", value: "Tourism, Hospitality & Creative Services" },
                    { label: "ICT & Digital Services", value: "ICT & Digital Services" },
                    { label: "Waste, Recycling & Circular Economy", value: "Waste, Recycling & Circular Economy" },
                    { label: "Retail, Distribution & Service Trades", value: "Retail, Distribution & Service Trades" },
                    { label: "Other/Specify", value: "Other/Specify" },
                  ]}
                  value={tempBusinessSector}
                  onChange={handleBusinessSectorChange}
                />
              </div>
            </div>
            <div className="flex gap-2 items-end justify-end">
              <button
                onClick={handleClearFilters}
                className="bg-[#be9609] hover:bg-[#997800] text-white px-4 py-1.5 rounded-md font-semibold transition-colors duration-200 h-fit"
              >
                Clear
              </button>
              <button
                onClick={handleApplyFilters}
                className="bg-[#137e81] hover:bg-[#0f6b6e] text-white px-4 py-1.5 rounded-md font-semibold transition-colors duration-200 h-fit"
              >
                Apply
              </button>
            </div>
          </div>

        </AccordionDetails>
      </Accordion>

      <div className="rounded-lg my-[15px] shadow w-full bg-white">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
          <h1 className="text-[0.875rem] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Micro Grants List
            {hasFilters && (
              <span className="ml-2 text-xs">(Filtered Results)</span>
            )}
          </h1>
          {shouldShowExportButton && (
            <button
              onClick={exportToExcel}
              disabled={isExporting}
              className="flex items-center gap-2 bg-[#137e81] hover:bg-[#0f6b6e] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export filtered Micro Grant data with tab-wise structure"
            >
              <Download size={16} />
              {isExporting ? "Exporting..." : "Export Excel"}
            </button>
          )}
        </div>

        <div className="p-[1.25rem] w-full">
          <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-[6px] w-full">
            <PaginationDropdown
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
              ]}
            />

            <input
              onChange={handleSearchChange}
              value={search}
              placeholder="Search"
              type="search"
              className="font-[Light] text-[14px] flex items-center bg-[#f1f4f8] border-[#ebeef2] border-[1px] rounded-md px-3 py-1 outline-none min-w-[300px]"
            />
          </div>

          <DataTable
            columns={columns}
            persistTableHead
            data={grants}
            customStyles={customStyles}
            highlightOnHover
            progressPending={false}
          />
          <GeneralFooter
            pagination={pagination}
            handlePageChange={handlePageChange}
            handlePageSizeChange={handlePageSizeChange}
            dataLength={grants.length}
          />
        </div>
      </div>

      <CustomConfirmModal
        isVisible={isQualifyModalVisible}
        onConfirm={handleQualify}
        onCancel={() => setQualifyModalVisible(false)}
        message="Are you sure you want to qualify this Micro grant?"
      />

      <CustomConfirmModal
        isVisible={isNotQualifyModalVisible}
        onConfirm={handleNotQualify}
        onCancel={() => setNotQualifyModalVisible(false)}
        message="Are you sure you want to mark this Micro grant as not qualified?"
      />

      <CustomConfirmModal
        isVisible={isModalVisible}
        onConfirm={handleDelete}
        onCancel={() => setModalVisible(false)}
        message="Are you sure you want to delete this Micro grant?"
      />
    </div>
  );
};

export default MicroGrantList;
